import type { Auth, User } from "firebase/auth";
import { useEffect, useRef, useState } from "react";
import * as api from "./api";
import { readingTime } from "./format";
import { go, useClock, useTitle } from "./hooks";
import {
  accessLine,
  afterReload,
  changeText,
  CHECK_RELOAD_MS,
  checkLine,
  groupIssues,
  type ImportAction,
  importFailure,
  ISSUE_ROW_LIMIT,
  KIND_TEXT,
  MODE_TEXT,
  pausedText,
  pauseText,
  RUN_COLUMNS,
  runChoices,
  runHint,
  SOURCE_MODE_TEXT,
  settingsText,
  STATE_TEXT,
} from "./sheetImport";
import { sortRows } from "./sort";
import { SortSelect, SortTh, useSort } from "./SortHeader";
import { type Failure, failureOf, Problem } from "./UserPages";
import { personName } from "./requests";
import { SalesImportPanel } from "./SalesImportPanel";
import { Skeleton } from "./Skeleton";

type Issues = { run: api.ImportRun; issues: api.ImportIssue[] };

const detailsBelow = (): boolean => {
  const title = document.getElementById("import-run-title");
  return !title || title.getBoundingClientRect().top > window.innerHeight;
};

/** Roadmap 020, design 26.6: the runs of the sheet import and the clean-up list. Only an administrator sees it (rule T-5). */
export function ImportPage({
  base,
  org,
  auth,
  user,
  canView,
  canEdit = false,
  canParity = false,
}: {
  base: string;
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  canView: boolean | undefined;
  canEdit?: boolean;
  canParity?: boolean;
}) {
  const [found, setFound] = useState<api.ImportRun[] | Failure | undefined>(undefined);
  const [check, setCheck] = useState<api.ImportCheck | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [picked, setPicked] = useState<string | null>(null);
  const [jump, setJump] = useState(0);
  const [sort, setSort] = useSort("sort", RUN_COLUMNS);
  const now = useClock();
  const reload = Math.floor(now.getTime() / CHECK_RELOAD_MS);
  useTitle(`Import · ${org.name}`);
  useEffect(() => {
    if (canView !== true) return;
    let live = true;
    user
      .getIdToken()
      .then(api.importRuns)
      .then((list) => {
        if (!live) return;
        setFound(list.runs);
        setCheck(list.last_check ?? null);
      })
      .catch((e) => {
        if (!live) return;
        const failure = failureOf(e);
        setFound((old) => afterReload(old, failure));
      });
    return () => {
      live = false;
    };
  }, [user, attempt, canView, reload]);
  if (canView === false || found === "hidden") {
    return (
      <main data-testid="import-hidden">
        <h1 tabIndex={-1}>Import</h1>
        <p>Only an administrator of the whole organization sees the sheet import.</p>
      </main>
    );
  }
  const runs = Array.isArray(found) ? found : null;
  const shown = picked ?? runs?.[0]?.id ?? null;
  return (
    <main className="import-page wide" data-testid="import-page">
      <h1 tabIndex={-1}>Import</h1>
      <p className="sub">
        Each hour the platform reads the sources of the organization and, in apply mode, writes the changes. The sources stay the record. You
        can change a field that a source holds, but the next import writes the source value again.
      </p>
      <SourcePanel user={user} canEdit={canEdit} timeZone={org.time_zone} onStarted={() => setAttempt((n) => n + 1)} />
      <SalesImportPanel user={user} timeZone={org.time_zone} />
      <LastCheck
        check={check}
        now={now}
        timeZone={org.time_zone}
        onOpen={(id) => {
          setPicked(id);
          setJump((n) => n + 1);
        }}
      />
      {found === undefined ? (
        <Skeleton shape="table" columns={5} />
      ) : typeof found === "string" ? (
        <Problem found={found} auth={auth} retry={() => setAttempt((n) => n + 1)} />
      ) : !found.length ? (
        <p data-testid="import-empty">No import yet. The first one runs in the next hour, or when an operator starts it.</p>
      ) : (
        <>
          <SortSelect id="import-select" columns={RUN_COLUMNS} sort={sort} onSort={setSort} fallback="Newest first" />
          <div className="table-scroll" tabIndex={0} role="region" aria-label="Import runs">
            <table className="list" data-testid="import-runs" data-cols="import-select">
              <thead>
                <tr>
                  {RUN_COLUMNS.map((column) => (
                    <SortTh key={column.key} column={column} sort={sort} onSort={setSort} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortRows(found, RUN_COLUMNS, sort).map((run) => (
                  <tr
                    key={run.id}
                    className={run.id === shown ? "picked" : undefined}
                    aria-current={run.id === shown ? "true" : undefined}
                    data-testid="import-run"
                    data-state={run.state}
                  >
                    <th scope="row">
                      <button
                        type="button"
                        className="link"
                        data-testid="import-run-pick"
                        onClick={() => {
                          setPicked(run.id);
                          setJump((n) => (detailsBelow() ? n + 1 : 0));
                        }}
                      >
                        {readingTime(run.started_at, org.time_zone)}
                      </button>
                      {run.fixture && <span className="sub"> · synthetic</span>}
                    </th>
                    <td data-label="Mode">{MODE_TEXT[run.mode]}</td>
                    <td data-label="Result" className={run.state === "ok" ? undefined : "error"}>
                      {STATE_TEXT[run.state]}
                    </td>
                    <td className="num" data-label="Rows">{run.counts.rows ?? "–"}</td>
                    <td className="num" data-label="New">{run.counts.accounts_new ?? 0}</td>
                    <td className="num" data-label="Changed">{run.counts.accounts_changed ?? 0}</td>
                    <td className="num" data-label="Off">{run.counts.accounts_off ?? 0}</td>
                    <td className="num" data-label="Pickups">{run.counts.pickups_new ?? 0}</td>
                    <td className="num" data-label="Issues">{run.issues}</td>
                    <td data-label="By">{personName(run.actor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {shown && <RunIssues key={shown} id={shown} jump={jump} base={base} org={org} auth={auth} user={user} />}
        </>
      )}
      {canParity && (
        <p data-testid="import-parity-link">
          The &quot;Sheet and app&quot; counts are on the{" "}
          <a href={`${base}/parity`} onClick={go}>
            Parity
          </a>{" "}
          page.
        </p>
      )}
    </main>
  );
}

const RUN_RELOAD_MS = [20_000, 60_000, 120_000];

/** Design 26.11 F8: the sources, their mode, Test access, Dry run, Run now and Pause. An older API has no source list. */
function SourcePanel({ user, canEdit, timeZone, onStarted }: { user: User; canEdit: boolean; timeZone?: string; onStarted: () => void }) {
  const [found, setFound] = useState<api.ImportSources | Failure | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ text: string; error: boolean } | null>(null);
  const [access, setAccess] = useState<api.SourceAccess[] | null>(null);
  const timers = useRef<number[]>([]);
  useEffect(() => {
    let live = true;
    user
      .getIdToken()
      .then(api.importSources)
      .then((list) => live && setFound(list))
      .catch((e) => live && setFound(failureOf(e)));
    return () => {
      live = false;
    };
  }, [user]);
  useEffect(() => () => timers.current.forEach((id) => window.clearTimeout(id)), []);
  if (found === undefined || found === "hidden") return null;
  if (typeof found === "string") {
    return (
      <p className="error" data-testid="import-sources-error">
        The import sources did not load. The runs below are still correct.
      </p>
    );
  }
  const act = async (action: ImportAction, label: string, work: (token: string) => Promise<string>) => {
    setBusy(true);
    setNote(null);
    const started = performance.now();
    console.info(`import: ${label}`);
    try {
      const text = await work(await user.getIdToken());
      console.info(`import: ${label} done in ${Math.round(performance.now() - started)} ms`);
      setNote({ text, error: false });
    } catch (e) {
      const status = e instanceof api.ApiError ? e.status : 0;
      console.warn(`import: ${label} failed with ${status || "no answer"} after ${Math.round(performance.now() - started)} ms`);
      setNote({ text: importFailure(e, action), error: true });
    } finally {
      setBusy(false);
    }
  };
  const start = (mode: "dry-run" | "apply") =>
    act(mode === "apply" ? "run" : "dry-run", mode === "apply" ? "run now" : "dry run", async (token) => {
      await api.runImport(token, mode);
      timers.current = RUN_RELOAD_MS.map((ms) => window.setTimeout(onStarted, ms));
      return `The ${mode === "apply" ? "import" : "dry run"} started. The new run shows in the list in about a minute.`;
    });
  const choices = runChoices(found.sources);
  const hint = runHint(choices, found.can_run);
  if (!found.sources.length) {
    return (
      <p data-testid="import-sources-empty">
        This organization has no import source. An operator adds it, with the adapter of the source shape.
      </p>
    );
  }
  return (
    <section className="import-sources" data-testid="import-sources" aria-labelledby="import-sources-title">
      <h2 id="import-sources-title">Sources</h2>
      <div className="table-scroll" tabIndex={0} role="region" aria-label="Import sources">
        <table className="list" data-testid="import-source-rows">
          <thead>
            <tr>
              <th scope="col">Source</th>
              <th scope="col">Adapter</th>
              <th scope="col">Settings</th>
              <th scope="col">Mode</th>
              <th scope="col">Schedule</th>
            </tr>
          </thead>
          <tbody>
            {found.sources.map((source) => (
              <tr key={source.kind} data-testid="import-source" data-kind={source.kind} data-mode={source.mode}>
                <th scope="row">{KIND_TEXT[source.kind] ?? source.kind}</th>
                <td data-label="Adapter">{source.label}</td>
                <td data-label="Settings" className="wrap">
                  {settingsText(source.settings)}
                </td>
                <td data-label="Mode" className="mode">
                  {canEdit ? (
                    <select
                      aria-label={`Mode of the ${source.kind} source`}
                      data-testid="import-source-mode"
                      value={source.mode}
                      disabled={busy}
                      onChange={(e) => {
                        const mode = e.target.value as api.SourceMode;
                        const name = KIND_TEXT[source.kind] ?? source.kind;
                        if (mode === "apply" && !window.confirm(`Write the changes of the ${name} source each hour?`)) return;
                        void act("mode", `mode ${source.kind} ${mode}`, async (token) => {
                          setFound(await api.setSourceMode(token, source.kind, mode));
                          return `The ${name} source is now: ${SOURCE_MODE_TEXT[mode]}.`;
                        });
                      }}
                    >
                      {(found.modes ?? (["off", "report", "apply"] as api.SourceMode[])).map((mode) => (
                        <option key={mode} value={mode}>
                          {SOURCE_MODE_TEXT[mode]}
                        </option>
                      ))}
                    </select>
                  ) : (
                    SOURCE_MODE_TEXT[source.mode]
                  )}
                </td>
                <td data-label="Schedule" className="wrap">
                  {pausedText(source, (at) => readingTime(at, timeZone))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {canEdit && (
        <p className="actions" data-testid="import-source-actions">
          <button
            type="button"
            data-testid="import-test"
            disabled={busy}
            onClick={() =>
              void act("test", "test access", async (token) => {
                const result = await api.testImportAccess(token);
                setAccess(result.access);
                return result.access.every((item) => item.ok) ? "Access is OK." : "Access has a problem. See below.";
              })
            }
          >
            Test access
          </button>{" "}
          <button type="button" data-testid="import-dry-run" disabled={busy || !choices.dryRun || !found.can_run} onClick={() => void start("dry-run")}>
            Dry run
          </button>{" "}
          <button
            type="button"
            data-testid="import-run-now"
            aria-describedby={!found.can_run ? "import-run-cell" : hint ? "import-run-hint" : undefined}
            disabled={busy || !choices.apply || !found.can_run}
            onClick={() => {
              if (window.confirm("Import now and write the changes?")) void start("apply");
            }}
          >
            Run now
          </button>{" "}
          <button
            type="button"
            data-testid="import-pause"
            data-paused={choices.paused ? "true" : "false"}
            disabled={busy}
            onClick={() =>
              void act("pause", choices.paused ? "restart" : "pause", async (token) => {
                setFound(await api.pauseImport(token, !choices.paused));
                return pauseText(!choices.paused, choices.apply && found.can_run);
              })
            }
          >
            {choices.paused ? "Restart" : "Pause"}
          </button>
        </p>
      )}
      {canEdit && !found.can_run && (
        <p className="sub" id="import-run-cell">
          Run now needs an update of this cell. The hourly import still runs.
        </p>
      )}
      {canEdit && hint && (
        <p className="sub" id="import-run-hint" data-testid="import-run-hint">
          {hint}
        </p>
      )}
      {note && (
        <p role={note.error ? "alert" : "status"} data-testid="import-source-note" className={note.error ? "error" : undefined}>
          {note.text}
        </p>
      )}
      {access && (
        <ul data-testid="import-access">
          {access.map((item) => (
            <li key={item.kind} className={item.ok ? undefined : "error"}>
              {accessLine(item)}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function LastCheck({
  check,
  now,
  timeZone,
  onOpen,
}: {
  check: api.ImportCheck | null;
  now: Date;
  timeZone?: string;
  onOpen: (id: string) => void;
}) {
  const line = checkLine(check, now, (at) => readingTime(at, timeZone));
  if (!check || !line) return null;
  const runId = check.run_id;
  return (
    <p data-testid="import-last-check" role={line.warn ? "status" : undefined} className={line.warn ? "error" : undefined}>
      {line.text}{" "}
      {runId ? (
        <button type="button" className="link" data-testid="import-last-check-run" onClick={() => onOpen(runId)}>
          {line.state}
        </button>
      ) : (
        line.state
      )}
    </p>
  );
}

function RunIssues({ id, jump, base, org, auth, user }: { id: string; jump: number; base: string; org: api.Bootstrap; auth: Auth; user: User }) {
  const [found, setFound] = useState<Issues | Failure | undefined>(undefined);
  const title = useRef<HTMLHeadingElement>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    user
      .getIdToken()
      .then((token) => api.importIssues(token, id))
      .then((list) => live && setFound(list))
      .catch((e) => live && setFound(failureOf(e)));
    return () => {
      live = false;
    };
  }, [user, id, attempt]);
  const loaded = typeof found === "object";
  useEffect(() => {
    if (!jump || !loaded) return;
    title.current?.scrollIntoView({ block: "start" });
    title.current?.focus({ preventScroll: true });
  }, [jump, loaded]);
  if (found === undefined) return <Skeleton shape="table" columns={5} label="Loading the issues" />;
  if (found === "hidden") return <p data-testid="import-run-missing">This run is gone.</p>;
  if (typeof found === "string") return <Problem found={found} auth={auth} retry={() => setAttempt((n) => n + 1)} />;
  const { run, issues } = found;
  const groups = groupIssues(issues);
  return (
    <section aria-labelledby="import-run-title" data-testid="import-run-detail">
      <h2 id="import-run-title" tabIndex={-1} ref={title}>
        {MODE_TEXT[run.mode]} of {readingTime(run.started_at, org.time_zone)}
      </h2>
      <p data-testid="import-run-result" className={run.state === "ok" ? undefined : "error"}>
        {STATE_TEXT[run.state]}. {changeText(run)}.
        {run.refused_reason && <span className="sub"> Why: {run.refused_reason}</span>}
      </p>
      {!groups.length ? (
        <p data-testid="import-no-issues">No issues in this run.</p>
      ) : (
        groups.map((group) => (
          <details key={group.code} className="import-group" data-testid="import-group" data-code={group.code}>
            <summary>
              <strong>{group.text}</strong> <span className="sub">· {group.issues.length} · owner: {group.owner}</span>
            </summary>
            <div className="table-scroll" tabIndex={0} role="region" aria-label={group.text}>
              <table className="list">
                <thead>
                  <tr>
                    <th scope="col">Account</th>
                    <th scope="col">Sheet</th>
                    <th scope="col">Row</th>
                    <th scope="col">Column</th>
                    <th scope="col">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {group.issues.slice(0, ISSUE_ROW_LIMIT).map((issue) => (
                    <tr key={issue.seq} data-testid="import-issue">
                      <th scope="row">
                        {issue.account_id ? (
                          <a href={`${base}/accounts/${issue.account_id}`} onClick={go}>
                            {issue.number}
                          </a>
                        ) : (
                          (issue.number ?? "–")
                        )}
                      </th>
                      <td data-label="Sheet">{issue.tab}</td>
                      <td data-label="Row">{issue.row ?? "–"}</td>
                      <td data-label="Column">{issue.column || "–"}</td>
                      <td data-label="Value">{issue.value ?? "–"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {group.issues.length > ISSUE_ROW_LIMIT && (
              <p className="sub">
                The first {ISSUE_ROW_LIMIT} of {group.issues.length}.
              </p>
            )}
          </details>
        ))
      )}
    </section>
  );
}
